/**
 * Transaction Rules management card for Settings → Automations.
 *
 * Users create IF-THEN rules that auto-categorize imported transactions
 * (e.g. "description contains Starbucks → Meals"). Rules are matched in
 * priority order (ascending) during Teller imports, PDF parses, and CSV
 * uploads; the "Re-apply to existing" button re-runs them across already-
 * imported rows.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Zap, Edit3, Loader2, Sparkles } from "lucide-react";

const FIELDS = [
  { v: "description", label: "Description" },
  { v: "bank_name",   label: "Bank name" },
  { v: "amount",      label: "Amount" },
];
const TYPES_BY_FIELD = {
  description: [
    { v: "contains",    label: "contains" },
    { v: "equals",      label: "equals" },
    { v: "starts_with", label: "starts with" },
    { v: "regex",       label: "regex" },
  ],
  bank_name: [
    { v: "contains",    label: "contains" },
    { v: "equals",      label: "equals" },
    { v: "starts_with", label: "starts with" },
  ],
  amount: [
    { v: "equals", label: "equals exactly" },
  ],
};

const BLANK = { name: "", match_field: "description", match_type: "contains", match_value: "", set_category: "", set_type: "", priority: 100, enabled: true };

export default function TransactionRulesCard() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | BLANK | existing rule
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [categories, setCategories] = useState([]);

  const load = async () => {
    try {
      const [{ data }, catsResp] = await Promise.all([
        api.get("/transaction-rules"),
        api.get("/categories").catch(() => ({ data: { categories: [] } })),
      ]);
      setRules(data.rules || []);
      // /categories returns builtin + custom; fall back to a tiny default list
      // so first-time users can still pick a value.
      const cats = (catsResp?.data?.categories || []).map((c) => (typeof c === "string" ? c : c.name));
      setCategories(cats.length ? cats : ["Meals", "Office", "Travel", "Software", "Utilities", "Payroll", "Revenue"]);
    } catch (e) {
      // non-fatal
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.match_value?.trim()) { toast.error("Match value is required"); return; }
    if (!editing.set_category?.trim()) { toast.error("Pick a category to set"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name || null,
        match_field: editing.match_field,
        match_type: editing.match_type,
        match_value: editing.match_value.trim(),
        set_category: editing.set_category.trim(),
        set_type: editing.set_type || null,
        priority: Number(editing.priority) || 100,
        enabled: editing.enabled !== false,
      };
      if (editing.id) {
        await api.put(`/transaction-rules/${editing.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/transaction-rules", payload);
        toast.success("Rule created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rule");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule? Transactions already categorized by it will keep their category.")) return;
    try {
      await api.delete(`/transaction-rules/${id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete rule");
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      await api.put(`/transaction-rules/${rule.id}`, { enabled: !rule.enabled });
      load();
    } catch (e) {
      toast.error("Could not toggle rule");
    }
  };

  const reapply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/transaction-rules/apply");
      toast.success(`Re-applied ${data.rules} rule(s) · ${data.touched} transaction${data.touched === 1 ? "" : "s"} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-apply failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="ll-card-flat p-6 mb-6" data-testid="transaction-rules-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
            <Sparkles size={11} /> Auto-categorization
          </div>
          <div className="font-display font-bold text-2xl mt-1">Transaction rules</div>
          <p className="text-sm text-[var(--ll-ink-soft)] mt-1 max-w-prose">
            Define IF-THEN rules so imported transactions arrive pre-categorized. First matching rule wins (lowest priority runs first). Rules apply automatically to Teller syncs, CSV uploads, and PDF parses.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={reapply}
            disabled={applying || !rules.length}
            data-testid="txn-rules-reapply"
            className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderColor: "var(--ll-border)" }}
          >
            {applying ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
            {applying ? "Running…" : "Re-apply to existing"}
          </button>
          <button
            type="button"
            onClick={() => setEditing({ ...BLANK })}
            data-testid="txn-rules-new"
            className="text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5"
          >
            <Plus size={13} /> New rule
          </button>
        </div>
      </div>

      {loading && <div className="ll-progress mb-2" />}
      {!loading && rules.length === 0 && !editing && (
        <div className="text-sm text-[var(--ll-ink-muted)] bg-[var(--ll-bg-card)] border px-3 py-6 text-center" style={{ borderColor: "var(--ll-border)" }} data-testid="txn-rules-empty">
          No rules yet. Click <strong>New rule</strong> to add your first auto-categorization.
        </div>
      )}

      {rules.length > 0 && (
        <div className="border divide-y" style={{ borderColor: "var(--ll-border)" }} data-testid="txn-rules-list">
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 bg-white" data-testid={`txn-rule-${r.id}`}>
              <button
                type="button"
                onClick={() => toggleEnabled(r)}
                data-testid={`txn-rule-toggle-${r.id}`}
                aria-pressed={r.enabled}
                className="relative w-9 h-5 rounded-full shrink-0"
                style={{ background: r.enabled ? "#10B981" : "#CBD5E1" }}
                title={r.enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
              >
                <span
                  className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                  style={{ transform: r.enabled ? "translateX(16px)" : "translateX(0)" }}
                />
              </button>
              <div className="font-mono-fin text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] w-10 shrink-0">
                #{r.priority}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-semibold">{r.name || `${r.match_field} ${r.match_type} "${r.match_value}"`}</span>
                </div>
                <div className="text-xs text-[var(--ll-ink-soft)] mt-0.5">
                  if <strong>{r.match_field}</strong> {r.match_type.replace("_", " ")} <code className="px-1 bg-[var(--ll-bg-card)]">{r.match_value}</code> → set category <strong>{r.set_category}</strong>
                  {r.set_type && <> + type <strong>{r.set_type}</strong></>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditing({ ...r })}
                data-testid={`txn-rule-edit-${r.id}`}
                className="p-1 hover:bg-[var(--ll-mint)]"
                title="Edit"
              >
                <Edit3 size={13} />
              </button>
              <button
                type="button"
                onClick={() => remove(r.id)}
                data-testid={`txn-rule-delete-${r.id}`}
                className="p-1 hover:bg-[var(--ll-error)] hover:text-white"
                title="Delete"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="mt-4 border p-4 bg-[var(--ll-bg-card)]"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid="txn-rule-editor"
        >
          <div className="grid md:grid-cols-2 gap-3">
            <label className="text-xs">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Name (optional)</span>
              <input
                value={editing.name || ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                data-testid="txn-rule-name"
                className="w-full px-2 py-1.5 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}
                placeholder="e.g. Coffee → Meals"
              />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Priority</span>
              <input
                type="number"
                value={editing.priority}
                onChange={(e) => setEditing({ ...editing, priority: e.target.value })}
                data-testid="txn-rule-priority"
                className="w-full px-2 py-1.5 border bg-white text-sm font-mono-fin" style={{ borderColor: "var(--ll-border)" }}
              />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">If</span>
              <select
                value={editing.match_field}
                onChange={(e) => {
                  const field = e.target.value;
                  const allowed = TYPES_BY_FIELD[field] || [];
                  setEditing({
                    ...editing,
                    match_field: field,
                    match_type: allowed.find((t) => t.v === editing.match_type) ? editing.match_type : allowed[0].v,
                  });
                }}
                data-testid="txn-rule-field"
                className="w-full px-2 py-1.5 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}
              >
                {FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
            </label>
            <label className="text-xs">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Match type</span>
              <select
                value={editing.match_type}
                onChange={(e) => setEditing({ ...editing, match_type: e.target.value })}
                data-testid="txn-rule-type"
                className="w-full px-2 py-1.5 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}
              >
                {(TYPES_BY_FIELD[editing.match_field] || []).map((t) => (
                  <option key={t.v} value={t.v}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs md:col-span-2">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Match value</span>
              <input
                value={editing.match_value || ""}
                onChange={(e) => setEditing({ ...editing, match_value: e.target.value })}
                data-testid="txn-rule-value"
                className="w-full px-2 py-1.5 border bg-white text-sm font-mono-fin" style={{ borderColor: "var(--ll-border)" }}
                placeholder={editing.match_field === "amount" ? "123.45" : "e.g. starbucks"}
              />
            </label>
            <label className="text-xs">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Then set category</span>
              <input
                list="txn-rule-cats"
                value={editing.set_category || ""}
                onChange={(e) => setEditing({ ...editing, set_category: e.target.value })}
                data-testid="txn-rule-category"
                className="w-full px-2 py-1.5 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}
                placeholder="Meals"
              />
              <datalist id="txn-rule-cats">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </label>
            <label className="text-xs">
              <span className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">And type (optional)</span>
              <select
                value={editing.set_type || ""}
                onChange={(e) => setEditing({ ...editing, set_type: e.target.value })}
                data-testid="txn-rule-settype"
                className="w-full px-2 py-1.5 border bg-white text-sm" style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">— no change —</option>
                <option value="income">income</option>
                <option value="expense">expense</option>
                <option value="transfer">transfer</option>
              </select>
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => setEditing(null)}
              data-testid="txn-rule-cancel"
              className="text-xs px-3 py-1.5 border hover:bg-white"
              style={{ borderColor: "var(--ll-border)" }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="txn-rule-save"
              className="text-xs px-4 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] disabled:opacity-60"
            >
              {saving ? "Saving…" : editing.id ? "Save changes" : "Create rule"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
